import { trierParUrgence } from "./accueil-urgence";
// src/lib/accueil-attend.js — « CE QUI VOUS ATTEND », le calcul.
//
// ⚠️ MODULE PUR — zéro I/O, zéro React, zéro Supabase. Entrée = les données
// déjà chargées par l'accueil, sortie = une liste de lignes.
//
// ⚠️⚠️ SOURCE UNIQUE (exigence Dimitri 2026-08-25) : le chiffre « à décider »
// du bandeau EST `construireAttend(...).length`. Le bandeau n'a pas son propre
// compte, la carte n'a pas le sien — les deux lisent CE tableau. C'est le
// défaut le plus répandu de l'admin actuel (le parc compté de cinq façons, le
// badge du stock qui dit zéro devant deux alertes rouges) ; il ne doit pas
// naître ici.
//
// ⚠️ UNE LIGNE DIT CE QUI RESTE À FAIRE, PAS CE QUI EST FAIT. « Un article à
// racheter » deviendrait « un article à déposer » une fois racheté — mais la
// famille LINGE est HORS LOT 1 (décision Dimitri 2026-08-25 : la fonction
// n'existe pas, et écrire « aucun article à racheter » ferait croire à une
// surveillance qui n'a pas lieu ; elle reviendra avec le lot B).
//
// ⚠️ UN CONTRÔLE DÉJÀ CONFIÉ N'ENTRE PAS. S'il porte un contrôleur désigné, il
// n'attend rien de l'admin — c'est la règle qui évite d'inonder l'écran.

/** Les tons, miroir de la maquette (terra · ambre · ciel). */
export const TON = Object.freeze({
  TERRA: "terra",
  AMBRE: "ambre",
  CIEL: "ciel",
});

/** Les natures, dans l'ORDRE d'affichage de la maquette. */
export const NATURE = Object.freeze({
  MENAGE_SANS_AGENT: "menage_sans_agent",
  DEMANDE_HORAIRE: "demande_horaire",
  INCIDENT_A_TRANCHER: "incident_a_trancher",
  AIRCOVER_A_SAISIR: "aircover_a_saisir",
  REPRISE_EN_ATTENTE: "reprise_en_attente",
  CONTROLE_SANS_PERSONNE: "controle_sans_personne",
  DEPART_REPORTE: "depart_reporte",
  // Ce qu'un VOYAGEUR nous dit — deux familles, une seule nature d'affichage :
  // pendant le séjour (« un problème », « une casse ») et à l'arrivée (état des
  // lieux). Dimitri : « c'est le même trou », et ça se lit au même endroit.
  SIGNALEMENT_VOYAGEUR: "signalement_voyageur",
});

// LOT 1 OPÉRATEUR — verdict FINAL Dimitri 2026-09-06 (revue arrêt 7, il
// revient sur Q9) : les propositions de l'opérateur N'ENTRENT PAS dans « Ce
// qui vous attend ». « Le tag "prête" sur le fil dans Messages suffit. Les
// deux blocs sont sur le même écran, à trois centimètres l'un de l'autre —
// compter deux fois la même chose, c'est du bruit. Ce qui m'attend, c'est ce
// qui n'a pas d'autre maison. Une réponse à un message a la sienne. »
// Leurs seules surfaces d'accueil : le tag « prête » (carte Messages), le
// bandeau-renvoi du fil, et le point d'état de la pilule d'en-tête.

function texte(v) {
  return typeof v === "string" ? v.trim() : "";
}

/**
 * « 24 août » — sans jour de semaine (banni hors salutation).
 * ⚠️ ET « 1er août », JAMAIS « 1 août » : `Intl` rend « 1 » en français, ce
 * qui n'est pas du français. Défaut vu à l'écran sur la vraie base.
 */
export function jourCourt(iso) {
  const [y, m, d] = String(iso || "")
    .split("-")
    .map(Number);
  if (!y || !m || !d) return "";
  const mois = new Intl.DateTimeFormat("fr-FR", {
    month: "long",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, d)));
  return `${d === 1 ? "1er" : d} ${mois}`;
}

/**
 * ⚠️ LE JOUR, QUAND CE N'EST PAS AUJOURD'HUI — défaut trouvé sur la VRAIE base
 * (prévisualisation, 2026-08-25) : trois ménages sans agent du même logement
 * remontaient en trois lignes RIGOUREUSEMENT IDENTIQUES — « Sur un Nuage ·
 * avant 11h » ×3 — sans aucun moyen de savoir laquelle était laquelle. Les
 * jeux d'essai ne le montraient pas : ils n'avaient qu'un run par logement.
 * Une ligne qui ne se distingue pas d'une autre ne désigne rien.
 */
function marqueurJour(run, todayIso) {
  const sd = run && run.scheduled_date;
  if (!sd || !todayIso || sd === todayIso) return null;
  return `le ${jourCourt(sd)}`;
}

/** « 17h » / « 17h30 » — LE format de l'application, jamais « 17:00 ». */
export function heureCourte(hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(texte(hhmm));
  if (!m) return "";
  return m[2] === "00" ? `${Number(m[1])}h` : `${Number(m[1])}h${m[2]}`;
}

// ── Les six natures, une fonction chacune ───────────────────────────────────
//
// Chacune reçoit ce qu'elle lit et rend un tableau de lignes. Une nature dont
// la donnée manque rend [] — JAMAIS une ligne inventée.

/**
 * Un ménage sans agent : aucun prestataire assigné. Une ligne par ménage.
 *
 * ⚠️ UN MÉNAGE TERMINÉ OU SAUTÉ N'ENTRE PAS, même sans adresse assignée : il
 * n'attend plus personne. Sans ce garde, un ménage fait remontait comme une
 * décision à prendre — et il aurait aussi été compté DEUX FOIS, ici et dans
 * « un contrôle sans personne ». Défaut attrapé par le test de source unique.
 */
const STATUTS_CLOS = new Set(["completed", "skipped"]);

export function menagesSansAgent(
  runs,
  { nomLogement, heureFin, todayIso } = {},
) {
  return (runs || [])
    .filter(
      (r) =>
        r && !texte(r.assigned_provider_email) && !STATUTS_CLOS.has(r.status),
    )
    .map((r) => {
      const h = heureFin ? heureCourte(heureFin(r)) : "";
      return {
        cle: `sans-agent:${r.id}`,
        nature: NATURE.MENAGE_SANS_AGENT,
        ton: TON.TERRA,
        titre: "Un ménage sans agent",
        // ⚠️ SANS la phrase d'habitude (décision Dimitri 2026-08-25) : la table
        // des règles est vide depuis la purge, elle mentirait.
        sousTitre: [
          nomLogement ? nomLogement(r.logement_id) : r.logement_id,
          marqueurJour(r, todayIso),
          h ? `avant ${h}` : null,
        ]
          .filter(Boolean)
          .join(" · "),
        cible: { ecran: "menage", runId: r.id },
      };
    });
}

/** Une demande d'horaire en attente — disparaît dès qu'elle est tranchée. */
export function demandesHoraire(demandes, { nomLogement } = {}) {
  return (
    (demandes || [])
      // ⚠️ 'pending_validation' est LE statut réel du serveur
      // (services_bookings) — le filtre initial ne connaissait que les formes
      // du prototype et aurait TU les vraies demandes (attrapé en relisant la
      // projection serveur, pas un écran).
      .filter(
        (d) =>
          d &&
          (d.statut === "attente" ||
            d.status === "pending" ||
            d.statut === "pending" ||
            d.status === "pending_validation"),
      )
      .map((d) => {
        const heure = d.heure || d.requested_time;
        const verbe =
          d.event_kind === "early_checkin"
            ? "demande à arriver à"
            : "demande à partir à";
        return {
          cle: `horaire:${d.id || d.booking_id}`,
          nature: NATURE.DEMANDE_HORAIRE,
          ton: TON.AMBRE,
          titre: "Une demande d'horaire",
          sousTitre: [
            nomLogement ? nomLogement(d.logement_id) : d.logement_id,
            d.voyageur && heure
              ? `${d.voyageur} ${verbe} ${heureCourte(heure)}`
              : null,
          ]
            .filter(Boolean)
            .join(" · "),
          cible: { ecran: "horaires", id: d.id || d.booking_id },
        };
      })
  );
}

/**
 * Un incident à trancher : au moins une dégradation « Non récupérable » dont
 * l'état financier n'est PAS qualifié. `estATrancher` est injecté (le helper
 * partagé `needsEtatFinancierQualif`) — ce module ne redéfinit pas la règle.
 */
export function incidentsATrancher(
  incidents,
  { estATrancher, nomLogement } = {},
) {
  if (typeof estATrancher !== "function") return [];
  return (incidents || [])
    .filter((i) => i && estATrancher(i))
    .map((i) => ({
      cle: `incident:${i.id}`,
      nature: NATURE.INCIDENT_A_TRANCHER,
      ton: TON.TERRA,
      titre: "Un incident à trancher",
      sousTitre: [
        nomLogement ? nomLogement(i.logement_id) : i.logement_id,
        texte(i.resume) || null,
      ]
        .filter(Boolean)
        .join(" · "),
      cible: { ecran: "incident", id: i.id },
    }));
}

/**
 * Une réponse AirCover à saisir : demande DÉCIDÉE, remboursement pas encore
 * saisi. ⚠️ La dérivation est injectée (`estAircoverASaisir`, le helper réel
 * de accueil-incident) — la première version testait `d.aircover === 'attente'`,
 * un champ DU PROTOTYPE qui n'existe pas en base : la ligne n'aurait JAMAIS
 * remonté sur une vraie donnée (attrapé en relisant le modèle, pas un écran).
 */
export function aircoverASaisir(
  incidents,
  { degradationsDe, nomLogement, estAircoverASaisir } = {},
) {
  if (typeof degradationsDe !== "function") return [];
  const aSaisir =
    typeof estAircoverASaisir === "function"
      ? estAircoverASaisir
      : (d) => d && d.aircover === "attente"; // compat harnais prototype
  const out = [];
  for (const i of incidents || []) {
    for (const d of degradationsDe(i) || []) {
      if (aSaisir(d, i)) {
        out.push({
          cle: `aircover:${i.id}:${d.id || d.type || out.length}`,
          nature: NATURE.AIRCOVER_A_SAISIR,
          // ⚠️ L'échéance COLORE la ligne : ambre sous trois jours, terracotta
          // dépassé — et dépassé ne veut pas dire effacé.
          ton:
            i._echeanceTon === "terra"
              ? TON.TERRA
              : i._echeanceTon === "ambre"
                ? TON.AMBRE
                : TON.CIEL,
          titre: "Une réponse AirCover à saisir",
          sousTitre: [
            nomLogement ? nomLogement(i.logement_id) : i.logement_id,
            texte(d.type) || null,
            texte(i._echeanceAircover) || "demande ouverte",
          ]
            .filter(Boolean)
            .join(" · "),
          cible: { ecran: "incident", id: i.id },
        });
      }
    }
  }
  return out;
}

/**
 * Une reprise en attente : contrôle avec points, PAS ENCORE DÉCIDÉ.
 * ⚠️ `decide_le` posé = l'admin a tranché (je repasse / je renvoie / c'est
 * bon, action controle_reprise_decider) → la ligne SORT, quel que soit le
 * choix. C'est le contrat de la feuille : on décide, la ligne disparaît.
 */
export function reprisesEnAttente(controles, { nomLogement, prenomDe } = {}) {
  return (controles || [])
    .filter(
      (c) =>
        c &&
        (c.statut === "a_reprendre" || c.statut === "reprise_en_cours") &&
        !c.decide_le,
    )
    .map((c) => {
      const qui =
        c.reprise_par === "controleur"
          ? "vous devez repasser"
          : `${(prenomDe ? prenomDe(c.agent_email) : null) || "l'agent"} doit repasser`;
      return {
        cle: `reprise:${c.id}`,
        nature: NATURE.REPRISE_EN_ATTENTE,
        ton: TON.TERRA,
        titre: "Une reprise en attente",
        sousTitre: [
          nomLogement ? nomLogement(c.logement_id) : c.logement_id,
          qui,
        ]
          .filter(Boolean)
          .join(" · "),
        cible: { ecran: "menage", runId: c.run_id, vue: "controle" },
      };
    });
}

/**
 * Un contrôle sans personne : ménage TERMINÉ, NON contrôlé, ET aucun
 * contrôleur désigné.
 * ⚠️ Un contrôle déjà confié n'entre pas — il n'attend rien de l'admin.
 */
export function controlesSansPersonne(
  runs,
  { estControle, nomLogement, limiteDe, todayIso } = {},
) {
  return (runs || [])
    .filter(
      (r) =>
        r &&
        r.status === "completed" &&
        !texte(r.controleur_designe_email) &&
        !(typeof estControle === "function" && estControle(r)),
    )
    .map((r) => ({
      cle: `ctrl-orphelin:${r.id}`,
      nature: NATURE.CONTROLE_SANS_PERSONNE,
      ton: TON.AMBRE,
      titre: "Un contrôle sans personne",
      sousTitre: [
        nomLogement ? nomLogement(r.logement_id) : r.logement_id,
        marqueurJour(r, todayIso),
        limiteDe ? limiteDe(r) : null,
      ]
        .filter(Boolean)
        .join(" · "),
      cible: { ecran: "menage", runId: r.id, vue: "controle" },
    }));
}

/**
 * CE QU'UN VOYAGEUR NOUS A DIT, ET QUE PERSONNE N'A ENCORE VU.
 *
 * Deux familles, une seule ligne de lecture (demande Dimitri 2026-09-05) :
 *   · `sejour`          — « un problème » ou « une casse » pendant le séjour ;
 *   · `etat_des_lieux`  — un état des lieux d'arrivée qui SIGNALE quelque chose.
 *
 * ⚠️ LE SERVEUR NE SERT QUE DU NON-TRAITÉ. Cette fonction ne refiltre pas :
 * une ligne présente ici est, par construction, une ligne qui attend. Poser un
 * second filtre côté écran, c'était s'offrir une divergence gratuite.
 *
 * ⚠️ LE TEXTE DU VOYAGEUR N'EST PAS DANS LE SOUS-TITRE, et c'est voulu : la
 * liste dit QU'IL Y A quelque chose et OÙ ; le texte se lit dans la feuille,
 * qui part au clic. C'est la règle de l'accueil (aucun détail pré-chargé), et
 * c'est aussi la bonne façon de traiter un texte libre qui peut contenir un
 * nom ou un numéro.
 *
 * ⚠️ AUCUN MONTANT, NULLE PART. Un signalement informe — il n'ouvre aucun
 * droit, ne chiffre rien, et ne renvoie vers aucun prélèvement.
 */
export function signalementsVoyageur(
  signalements,
  { nomLogement, todayIso } = {},
) {
  const titres = {
    casse: "Un voyageur signale une casse",
    probleme: "Un voyageur signale un problème",
    etat_des_lieux: "Un état des lieux d'arrivée à lire",
  };
  return (signalements || [])
    .filter((s) => s && s.id && s.famille)
    .map((s) => ({
      cle: `signalement:${s.famille}:${s.id}`,
      nature: NATURE.SIGNALEMENT_VOYAGEUR,
      // La casse et le problème appellent une réaction rapide (terracotta) ;
      // l'état des lieux d'arrivée est un constat à lire (ambre).
      ton: s.famille === "etat_des_lieux" ? TON.AMBRE : TON.TERRA,
      titre: titres[s.kind] || "Un voyageur nous a écrit",
      sousTitre: [
        nomLogement ? nomLogement(s.logement_id) : s.logement_id,
        quandRecu(s.submitted_at, todayIso),
        s.photos_count > 0
          ? `${s.photos_count} photo${s.photos_count > 1 ? "s" : ""}`
          : null,
      ]
        .filter(Boolean)
        .join(" · "),
      cible: { ecran: "signalement", famille: s.famille, id: s.id },
    }));
}

/**
 * « aujourd'hui » · « hier » · « le 3 septembre ».
 * ⚠️ Une date illisible ne rend RIEN plutôt qu'une phrase fausse.
 */
export function quandRecu(iso, todayIso) {
  if (!iso || !todayIso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const jour = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Paris",
  }).format(d);
  if (jour === todayIso) return "aujourd'hui";
  const veille = new Date(`${todayIso}T12:00:00Z`);
  veille.setUTCDate(veille.getUTCDate() - 1);
  const hier = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Paris",
  }).format(veille);
  if (jour === hier) return "hier";
  const court = jourCourt(jour);
  return court ? `le ${court}` : "";
}

/**
 * LA LISTE — dans l'ordre de la maquette, qui fait foi.
 *
 * ⚠️ Le chiffre « à décider » du bandeau est la LONGUEUR de ce tableau. Aucun
 * appelant ne recompte : `construireAttend(...).length`, et rien d'autre.
 */
/**
 * LES VEILLES DE L'OPÉRATEUR, en lignes de « Ce qui vous attend ».
 *
 * ⚠️ POURQUOI ELLES ENTRENT, ALORS QUE LES PROPOSITIONS N'ENTRENT PAS.
 * Le verdict FINAL du 2026-09-06 dit : « ce qui m'attend, c'est ce qui n'a pas
 * d'autre maison ». Les PROPOSITIONS en ont trois (le tag « prête », le
 * bandeau-renvoi du fil, le point d'état de la pastille) — elles restent
 * dehors. Les VEILLES n'en ont AUCUNE : elles vivent dans un écran que Dimitri
 * n'ouvre pas le matin. La règle est donc respectée, pas levée.
 *
 * ⛔ NE PAS EN CONCLURE que les propositions peuvent revenir « puisque les
 * veilles y sont ». Le critère n'est pas la provenance, c'est l'absence
 * d'autre maison.
 *
 * La ligne garde son `type`, ses `jours` et sa `destination` : le barème
 * d'urgence (src/lib/accueil-urgence.js) en a besoin, et la ligne doit rester
 * cliquable vers l'écran exact.
 */
export function veillesOperateur(alertes, { nomLogement } = {}) {
  return (alertes || [])
    .filter((a) => a && a.type && a.phrase)
    .map((a) => ({
      cle: `veille:${a.type}:${a.id}`,
      nature: a.type,
      type: a.type,
      // La provenance s'AFFICHE (pastille) mais n'entre pas dans le tri.
      source: "operateur",
      ton: a.gravite === "haute" ? TON.TERRA : TON.CIEL,
      titre: a.phrase,
      sousTitre: [
        a.logement_id && nomLogement
          ? nomLogement(a.logement_id)
          : a.logement_id,
      ]
        .filter(Boolean)
        .join(" · "),
      jours: a.jours,
      destination: a.destination || null,
      // ── DE QUOI LA TAIRE ────────────────────────────────────────────────
      // ⚠️ On PORTE la famille et la cible, on ne les redécoupe pas depuis
      // `cle`. Reconstruire « veille:type:id » à l'envers marcherait jusqu'au
      // premier identifiant contenant un « : » — et l'échec serait muet : le
      // silence se poserait sur une clé qui ne correspond à rien, l'alerte
      // resterait, et on croirait à un bug d'affichage.
      taire: { famille: a.type, cible: a.id == null ? null : String(a.id) },
    }));
}

// ══ REGROUPER CE QUI SE RÉPÈTE ═════════════════════════════════════════════
//
// Dimitri, 2026-09-12 : « regroupe ce qui se répète : "10 logements sans barème
// de taxe" sur une ligne, pas dix. »
//
// ⚠️ Dix lignes identiques ne disent pas dix fois plus qu'une seule : elles
// disent la même chose dix fois, et elles noient les huit autres natures. Le
// groupe garde SES lignes (l'écran les déplie, et chacune reste taisable
// individuellement) — on ne perd rien, on arrête de répéter.
//
// Le libellé du groupe vient de la ligne la plus urgente : c'est elle qui a été
// écrite pour être lue, et la reformuler ici créerait une seconde vérité sur le
// même fait.
const LIBELLES_GROUPE = {
  logement_sans_bareme: (n) => `${n} logements sans barème de taxe de séjour`,
  formulaire_non_rempli: (n) => `${n} formulaires d'arrivée non remplis`,
  formulaire_jamais_envoye: (n) => `${n} formulaires jamais envoyés`,
  menage_jamais_clos: (n) => `${n} ménages jamais clôturés`,
  depart_sans_menage: (n) => `${n} départs sans ménage prévu`,
  incident_jamais_juge: (n) => `${n} incidents jamais jugés`,
  rachat_jamais_fait: (n) => `${n} rachats jamais faits`,
  avis_negatif_non_traite: (n) => `${n} avis négatifs non traités`,
  prospect_abandonne: (n) => `${n} prospects abandonnés`,
};

export function groupeParNature(lignes) {
  const ordre = [];
  const par = new Map();
  for (const l of lignes || []) {
    const k = l && l.nature ? String(l.nature) : "autre";
    if (!par.has(k)) {
      par.set(k, []);
      ordre.push(k);
    }
    par.get(k).push(l);
  }
  return ordre.map((k) => {
    const groupe = par.get(k);
    const n = groupe.length;
    const faire = LIBELLES_GROUPE[k];
    return {
      nature: k,
      n,
      // ⚠️ UNE SEULE ligne ne se résume pas : la résumer perdrait le nom du
      // logement, qui est tout ce qui la rend utile.
      titre: n === 1 || !faire ? groupe[0].titre : faire(n),
      // Le sous-titre d'un groupe de plusieurs serait celui d'UN seul — donc
      // faux pour les autres. On le laisse tomber plutôt que de mentir.
      sousTitre: n === 1 ? groupe[0].sousTitre : "",
      ton: groupe[0].ton,
      lignes: groupe,
    };
  });
}

/**
 * Sépare ce qui se règle AUJOURD'HUI de ce qui se SURVEILLE.
 *
 * ⚠️ La ligne de partage est la NATURE de la ligne, pas son ancienneté : une
 * veille constate qu'une chose traîne et se rattrape ; un fait du jour se règle
 * d'un geste. Trier sur l'âge aurait fait basculer un ménage sans agent de la
 * semaine dernière dans la surveillance, alors qu'il se règle en un clic.
 *
 * ⚠️ ET LES MESSAGES SORTENT DES DEUX. Dimitri, 2026-09-12 : « un message
 * attend une réponse depuis 73 jours n'a rien à faire dans Ce qui vous attend.
 * J'ai déjà une section Messages en attente juste en dessous. » Compter deux
 * fois la même chose, c'est du bruit — la règle est déjà celle des propositions.
 *
 * @returns {{attend:Array, surveiller:Array, messages:Array}}
 */
export const NATURE_MESSAGE = "fil_sans_reponse";

export function partagerAttend(lignes) {
  const attend = [];
  const veille = [];
  const messages = [];
  for (const l of lignes || []) {
    if (!l) continue;
    if (l.nature === NATURE_MESSAGE) {
      messages.push(l);
      continue;
    }
    (l.source === "operateur" ? veille : attend).push(l);
  }
  return { attend, surveiller: groupeParNature(veille), messages };
}

export function construireAttend(donnees = {}, aides = {}) {
  const {
    runs,
    demandesHoraire: dem,
    incidents,
    controles,
    signalements,
    // LES VEILLES — cf. veillesOperateur ci-dessus pour le POURQUOI.
    veilles,
    // LOT 1 OPÉRATEUR — verdict FINAL 2026-09-06 (arrêt 7) : les propositions
    // NE FONT PLUS DE LIGNE ici (cf. note sous NATURE). Le paramètre reste,
    // INERTE côté source, pour le seul mécanisme d'ABSORPTION ci-dessous —
    // monté-dormant, aucun appelant ne le nourrit au lot 1.
    propositions,
  } = donnees;
  // ══ DEUX LISTES, PAS UNE ═══════════════════════════════════════════════
  //
  // Dimitri, 2026-09-12 : « j'ai vingt-deux lignes d'affilée, je n'en lis
  // aucune. Mais le problème n'est pas le volume, c'est le MÉLANGE. »
  //
  //   « Ce qui vous attend » doit être ce que je fais AUJOURD'HUI. Un ménage à
  //   assigner, une demande à trancher. Des choses qui se règlent maintenant.
  //   Ce que tu y mets, c'est de la SURVEILLANCE : un formulaire pas rempli, un
  //   barème absent depuis des mois, un ménage jamais clos depuis 125 jours.
  //   Ça ne m'attend pas. Ça se corrige quand j'ai le temps.
  //
  // ⚠️ LA LIGNE DE PARTAGE EST LA NATURE DE LA SOURCE, pas son ancienneté ni sa
  // gravité. Les faits du jour (un ménage sans agent, une demande d'heure, un
  // incident à trancher) naissent d'un événement daté et se règlent d'un geste.
  // Les VEILLES, elles, constatent qu'une chose traîne : elles ne se règlent
  // pas « maintenant », elles se rattrapent. Trier sur l'âge aurait fait
  // basculer un ménage sans agent de la semaine dernière dans la surveillance,
  // alors qu'il se règle en un clic.
  const lignesDuJour = [
    // EN TÊTE : ce qu'un voyageur nous dit passe avant l'organisation interne.
    ...signalementsVoyageur(signalements, aides),
    ...menagesSansAgent(runs, aides),
    ...departsReportes(runs, aides),
    ...demandesHoraire(dem, aides),
    ...incidentsATrancher(incidents, aides),
    ...aircoverASaisir(incidents, aides),
    ...reprisesEnAttente(controles, aides),
    ...controlesSansPersonne(runs, aides),
  ];
  const lignes = [...lignesDuJour, ...veillesOperateur(veilles, aides)];
  // ABSORPTION D3 — MÉCANISME EN RÉSERVE (monté-dormant) : une proposition
  // VIVANTE qui porterait `fait_cle` absorbe la ligne de fait qu'elle traite
  // (le fait disparaît tant qu'elle vit, il revient si elle meurt — le
  // serveur ne sert que du vivant, la clé sort donc d'elle-même). Au lot 1,
  // AUCUN appelant ne nourrit `propositions` et `fait_cle` reste nul (les
  // messages ne sont pas des lignes de « Ce qui vous attend ») — il servira
  // au premier déclencheur de fait.
  const absorbees = new Set(
    (propositions || []).map((p) => p && texte(p.fait_cle)).filter(Boolean),
  );
  const retenues =
    absorbees.size === 0 ? lignes : lignes.filter((l) => !absorbees.has(l.cle));
  // L'ORDRE EST CELUI DU COÛT, pas celui de la source ni celui de la maquette
  // (règle Dimitri 2026-09-07). Le barème est nommé et testé à part :
  // src/lib/accueil-urgence.js.
  //
  // ⚠️ CETTE FONCTION REND TOUJOURS UN TABLEAU NU. La séparation « aujourd'hui
  // / à surveiller » vit dans `partagerAttend` ci-dessous, appelée par l'écran.
  // Enrichir ce tableau de propriétés aurait marché — et piégé le lecteur
  // suivant, qui aurait comparé la liste à `[]` sans comprendre pourquoi ça
  // échoue. Une fonction de plus coûte moins qu'une valeur qui n'est pas ce
  // qu'elle paraît.
  return trierParUrgence(retenues);
}

/**
 * « UN DÉPART A BOUGÉ » (arbitrage Dimitri 2026-08-27) — le signal
 * `depart_reporte_au` posé par la cascade quand elle NE déplace PAS le
 * ménage (en cours, fait, ou date passée) : un fait qui attend une décision
 * humaine — exactement ce que porte « Ce qui vous attend ».
 */
export function departsReportes(runs, { nomLogement } = {}) {
  return (runs || [])
    .filter((r) => r && texte(r.depart_reporte_au))
    .map((r) => ({
      cle: `depart-reporte:${r.id}`,
      nature: NATURE.DEPART_REPORTE,
      ton: TON.TERRA,
      titre: "Un départ a bougé",
      sousTitre: [
        nomLogement ? nomLogement(r.logement_id) : r.logement_id,
        `nouveau départ ${jourCourt(r.depart_reporte_au)}`,
      ]
        .filter(Boolean)
        .join(" · "),
      cible: { ecran: "menage", runId: r.id },
    }));
}
